import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { getAppointments } from '../services/api';
import { APPOINTMENT_STATE_CONFIG as STATE_CONFIG } from '../constants/appointmentStates';

const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const START_HOUR = 7;
const END_HOUR = 21; // exclusivo
const SLOT_MINUTES = 15;
const FREE_COLOR_LIGHT = '#e8f5e9';
const FREE_COLOR_DARK = '#1b3a1f';
const OUT_OF_SCHEDULE_LIGHT = '#f0f0f0';
const OUT_OF_SCHEDULE_DARK = '#2a2a2a';

// Lunes de la semana que contiene `date`, a las 00:00 local.
const startOfWeek = (date) => {
  const d = new Date(date);
  const dow = (d.getDay() + 6) % 7; // 0=Lunes
  d.setDate(d.getDate() - dow);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Vista "zoom out" de la agenda semanal: una fila por cada 15 minutos (igual
// granularidad que CalendarGrid), coloreada con el color REAL del estado del
// turno que ocupa esa franja — no un promedio de "ocupación" genérico, para
// no perder la información de qué hay ahí (pendiente vs confirmado vs
// atendido) de un vistazo. Complementa a CalendarGrid (que solo muestra
// ~5hs por pantalla sin scroll); esta muestra toda la semana entera.
export default function WeekOverviewGrid({ doctorId, businessHours, onSlotClick, onAppointmentClick }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    return d;
  }, [weekStart]);

  const load = useCallback(() => {
    if (!doctorId) return;
    setLoading(true);
    getAppointments({
      doctor_id: doctorId,
      start_date: weekStart.toISOString(),
      end_date: weekEnd.toISOString(),
      limit: 500,
    })
      .then(r => setAppointments(r.data?.items || []))
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false));
  }, [doctorId, weekStart, weekEnd]);

  useEffect(() => { load(); }, [load]);

  // Días (0=Lunes) y hora de inicio del médico seleccionado — mismo
  // criterio ya aplicado en CalendarGrid.js (vista Detallada): si el
  // médico no tiene NINGÚN horario configurado un día, esa columna se
  // oculta; el rango de horas visibles arranca 1hs antes de su inicio
  // real (margen de seguridad por si hay algún turno excepcional cargado
  // antes de horario). Sin horario configurado (businessHours === false),
  // no se oculta ni recorta nada. Pedido explícito del usuario 2026-10-30.
  const workedWeekdays = useMemo(() => {
    if (!businessHours || businessHours.length === 0) return null; // null = sin info, no filtrar
    const jsDays = new Set(businessHours.flatMap(s => s.daysOfWeek));
    // Convertir de jsDay (0=domingo) a dayIndexMonFirst (0=lunes), igual
    // que isWithinSchedule hace a la inversa más abajo.
    return new Set([...jsDays].map(jsDay => (jsDay + 6) % 7));
  }, [businessHours]);

  const { visibleStartHour, visibleEndHour } = useMemo(() => {
    if (!businessHours || businessHours.length === 0) return { visibleStartHour: START_HOUR, visibleEndHour: END_HOUR };
    const starts = businessHours.map(s => parseInt(s.startTime.split(':')[0], 10));
    const ends = businessHours.map(s => {
      const [h, m] = s.endTime.split(':').map(Number);
      return m > 0 ? h + 1 : h; // redondea hacia arriba si no es hora en punto
    });
    return {
      visibleStartHour: Math.max(START_HOUR, Math.min(...starts) - 1),
      visibleEndHour: Math.min(END_HOUR, Math.max(...ends) + 1),
    };
  }, [businessHours]);

  // Cada slot es {hour, minute} — visibleStartHour:00 a visibleEndHour:00 en pasos de 15min.
  const slots = useMemo(() => {
    const arr = [];
    for (let h = visibleStartHour; h < visibleEndHour; h++) {
      for (let m = 0; m < 60; m += SLOT_MINUTES) arr.push({ hour: h, minute: m });
    }
    return arr;
  }, [visibleStartHour, visibleEndHour]);

  const allDays = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [weekStart]);

  // Índices (0=Lunes) de las columnas realmente visibles — se preserva el
  // dayIndex ORIGINAL de cada una (no se re-numeran) porque isWithinSchedule/
  // cellColor/cellLabel ya esperan ese índice para consultar businessHours.
  const visibleDayIndexes = useMemo(() => {
    if (!workedWeekdays) return [0, 1, 2, 3, 4, 5, 6];
    const idxs = [0, 1, 2, 3, 4, 5, 6].filter(i => workedWeekdays.has(i));
    return idxs.length > 0 ? idxs : [0, 1, 2, 3, 4, 5, 6]; // nunca ocultar TODOS los días
  }, [workedWeekdays]);

  // businessHours viene de useCalendarData: array de {daysOfWeek:[0-6 dom=0], startTime, endTime} o false
  const isWithinSchedule = useCallback((dayIndexMonFirst, hour, minute) => {
    if (!businessHours) return true; // sin horario configurado -> no bloquear nada
    const jsDay = (dayIndexMonFirst + 1) % 7; // 0=Domingo, como Date.getDay()
    const t = hour * 60 + minute;
    return businessHours.some(slot => {
      if (!slot.daysOfWeek.includes(jsDay)) return false;
      const [sh, sm] = slot.startTime.split(':').map(Number);
      const [eh, em] = slot.endTime.split(':').map(Number);
      return t >= sh * 60 + sm && t < eh * 60 + em;
    });
  }, [businessHours]);

  // Un turno por slot de 15min que ocupa — última ocurrencia gana si hay
  // solapamiento (sobreturno), que es raro pero posible.
  const slotAppointmentMap = useMemo(() => {
    const map = {};
    for (const ap of appointments) {
      const start = new Date(ap.start_time + 'Z');
      const end = new Date(start.getTime() + ap.duration_minutes * 60000);
      let cursor = new Date(start);
      cursor.setMinutes(Math.floor(cursor.getMinutes() / SLOT_MINUTES) * SLOT_MINUTES, 0, 0);
      while (cursor < end) {
        const dow = (cursor.getDay() + 6) % 7;
        const key = `${dow}-${cursor.getHours()}-${cursor.getMinutes()}`;
        map[key] = ap;
        cursor = new Date(cursor.getTime() + SLOT_MINUTES * 60000);
      }
    }
    return map;
  }, [appointments]);

  const getSlotAppointment = (dayIndex, hour, minute) => slotAppointmentMap[`${dayIndex}-${hour}-${minute}`];

  // El color SIEMPRE refleja el estado real del turno, igual criterio que
  // CalendarGrid.js — is_overbooked ya no pisa el color acá (mismo bug real
  // reportado por el usuario 2026-10-27: un sobreturno atendido y uno
  // pendiente se veían idénticos, ambos naranja).
  const cellColor = (dayIndex, hour, minute) => {
    const ap = getSlotAppointment(dayIndex, hour, minute);
    if (ap) {
      return STATE_CONFIG[ap.state]?.color || '#78909c';
    }
    if (!isWithinSchedule(dayIndex, hour, minute)) return isDark ? OUT_OF_SCHEDULE_DARK : OUT_OF_SCHEDULE_LIGHT;
    return isDark ? FREE_COLOR_DARK : FREE_COLOR_LIGHT;
  };

  const cellLabel = (dayIndex, hour, minute) => {
    const ap = getSlotAppointment(dayIndex, hour, minute);
    if (ap) {
      const who = ap.patient?.name || (ap.patient_id ? 'Paciente' : 'Bloqueado');
      const stateLabel = STATE_CONFIG[ap.state]?.label || ap.state;
      const overbookedSuffix = ap.is_overbooked ? ' · Sobreturno' : '';
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} — ${who} (${stateLabel})${overbookedSuffix}`;
    }
    if (!isWithinSchedule(dayIndex, hour, minute)) return 'Fuera de horario';
    return 'Libre — click para crear turno';
  };

  const todayKey = new Date().toDateString();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => setWeekStart(startOfWeek(new Date()))}>
          <TodayIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" fontWeight={600} sx={{ ml: 1 }}>
          {weekStart.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })} – {allDays[6].toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
        {/* gridTemplateColumns usa la cantidad REAL de columnas visibles
            (puede ser menos de 7 si se ocultan días sin horario) — pero el
            dayIndex que se le pasa a isWithinSchedule/cellColor/cellLabel/
            getSlotAppointment más abajo sigue siendo el ÍNDICE ORIGINAL
            0=lunes..6=domingo (no la posición en este array), porque esas
            funciones consultan businessHours y slotAppointmentMap con ese
            índice real. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: `56px repeat(${visibleDayIndexes.length}, 1fr)`, minWidth: 80 + visibleDayIndexes.length * 80 }}>
          {/* Header de días */}
          <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }} />
          {visibleDayIndexes.map((dayIndex) => {
            const d = allDays[dayIndex];
            return (
              <Box key={dayIndex} sx={{
                textAlign: 'center', py: 1, borderBottom: '2px solid', borderColor: 'divider',
                bgcolor: d.toDateString() === todayKey ? 'primary.50' : 'background.paper',
                position: 'sticky', top: 0, zIndex: 1,
              }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block' }}>
                  {DAY_LABELS[dayIndex]}
                </Typography>
                <Typography variant="caption" color={d.toDateString() === todayKey ? 'primary.main' : 'text.secondary'} fontWeight={d.toDateString() === todayKey ? 700 : 400}>
                  {d.getDate()}
                </Typography>
              </Box>
            );
          })}

          {/* Filas por slot de 15min, agrupadas visualmente por hora con una línea más marcada */}
          {slots.map(({ hour, minute }) => {
            const isHourStart = minute === 0;
            return (
              <React.Fragment key={`${hour}-${minute}`}>
                <Box sx={{
                  textAlign: 'right', pr: 1,
                  borderRight: '1px solid', borderColor: 'divider',
                  borderTop: isHourStart ? '1px solid' : 'none',
                  borderTopColor: 'divider',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
                }}>
                  {isHourStart && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1, pt: 0.25 }}>
                      {String(hour).padStart(2, '0')}:00
                    </Typography>
                  )}
                </Box>
                {visibleDayIndexes.map((dayIndex) => {
                  const d = allDays[dayIndex];
                  const inSchedule = isWithinSchedule(dayIndex, hour, minute);
                  const ap = getSlotAppointment(dayIndex, hour, minute);
                  return (
                    <Tooltip key={dayIndex} title={cellLabel(dayIndex, hour, minute)}>
                      <Box
                        onClick={() => {
                          if (ap) { onAppointmentClick?.(ap); return; }
                          if (!inSchedule) return;
                          const clickDate = new Date(d);
                          clickDate.setHours(hour, minute, 0, 0);
                          onSlotClick?.(clickDate);
                        }}
                        sx={{
                          height: SLOT_MINUTES === 15 ? 9 : 14,
                          borderTop: isHourStart ? '1px solid' : 'none',
                          borderTopColor: 'divider',
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          bgcolor: cellColor(dayIndex, hour, minute),
                          cursor: (ap || inSchedule) ? 'pointer' : 'default',
                          '&:hover': (ap || inSchedule) ? { filter: 'brightness(0.93)' } : {},
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
